import {
    Box,
    Chip,
    Collapse,
    Divider,
    LinearProgress,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    AutoAwesome,
    BugReport,
    ExpandLess,
    ExpandMore,
    PersonOutline,
    TokenOutlined,
    QueryStatsOutlined,
} from '@mui/icons-material';
import React, { useCallback, useEffect, useState } from 'react';
import { getAIUsage } from '../../modules/aiConfig';

const FEATURE_META = {
    testCaseGeneration: {
        label: 'Test Case Generation',
        icon: <AutoAwesome sx={{ fontSize: 12 }} />,
        color: 'primary',
    },
    storyGeneration: {
        label: 'Story Generation',
        icon: <AutoAwesome sx={{ fontSize: 12 }} />,
        color: 'primary',
    },
    defectIntelligence: {
        label: 'Defect Intelligence',
        icon: <BugReport sx={{ fontSize: 12 }} />,
        color: 'secondary',
    },
};

function formatTokens(n) {
    if (n == null || n === 0) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return String(n);
}

function pct(used, limit) {
    if (!limit || !used) return null;
    return Math.min(100, Math.round((used / limit) * 100));
}

function usageColor(percent) {
    if (percent == null) return 'primary';
    if (percent >= 90) return 'error';
    if (percent >= 70) return 'warning';
    return 'primary';
}

function transformUsage(rawArray = [], usersMap = {}) {
    const byUser = {};

    for (const row of rawArray) {
        const userId = row.user_id ?? row.userId;
        const featureKey = row.feature_key ?? row.featureKey;
        const tokens = parseInt(row.total_tokens ?? row.totalTokens, 10) || 0;
        const requests = parseInt(row.request_count ?? row.requestCount, 10) || 0;

        if (!byUser[userId]) {
            byUser[userId] = {
                userId,
                name: `${row.first_name ?? ''} ${row.last_name ?? 'Unknown user'}`,
                totalTokens: 0,
                requestCount: 0,
                features: [],
            };
        }

        byUser[userId].totalTokens += tokens;
        byUser[userId].requestCount += requests;
        byUser[userId].features.push({ featureKey, totalTokens: tokens, requestCount: requests });
    }

    const users = Object.values(byUser).sort((a, b) => b.totalTokens - a.totalTokens);

    return {
        totalTokens: users.reduce((s, u) => s + u.totalTokens, 0),
        requestCount: users.reduce((s, u) => s + u.requestCount, 0),
        users,
    };
}

function userInitials(name, userId) {
    if (name) {
        const parts = name.trim().split(' ');
        return parts.length >= 2
            ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
            : name.slice(0, 2).toUpperCase();
    }
    return (userId ?? '??').slice(0, 2).toUpperCase();
}

const StatCard = ({ icon, label, value, limit, sublabel, mono = false }) => {
    const percent = pct(value, limit);
    const color = usageColor(percent);

    return (
        <Box sx={{
            flex: 1, p: 1.75,
            bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider',
            borderRadius: 2, minWidth: 0,
            display: 'flex', flexDirection: 'column', gap: 0.5,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ color: 'text.disabled', display: 'flex' }}>{icon}</Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
                    {label}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    fontFamily={mono ? 'monospace' : undefined}
                    lineHeight={1.2}
                >
                    {mono ? formatTokens(value) : (value ?? 0).toLocaleString()}
                </Typography>
                {limit != null && (
                    <Typography variant="caption" color="text.secondary">
                        / {formatTokens(limit)}
                    </Typography>
                )}
            </Box>

            {percent != null ? (
                <Tooltip title={`${percent}% of limit used`} placement="top">
                    <LinearProgress
                        variant="determinate"
                        value={percent}
                        color={color}
                        sx={{ mt: 0.5, height: 3, borderRadius: 2 }}
                    />
                </Tooltip>
            ) : (
                <Box sx={{ mt: 0.5, height: 3, bgcolor: 'action.hover', borderRadius: 2 }} />
            )}

            <Typography variant="caption" color="text.disabled" noWrap sx={{ mt: 0.25 }}>
                {sublabel}
            </Typography>
        </Box>
    );
};

const FeatureBadge = ({ featureKey, count, tokens }) => {
    const meta = FEATURE_META[featureKey] ?? { label: featureKey, icon: null };
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            px: 1, py: 0.5,
            bgcolor: 'background.default',
            border: '1px solid', borderColor: 'divider',
            borderRadius: 1.5,
        }}>
            {meta.icon && <Box sx={{ color: 'primary.main', display: 'flex' }}>{meta.icon}</Box>}
            <Typography variant="caption" fontWeight={500}>{meta.label}</Typography>
            <Typography variant="caption" color="text.disabled" fontFamily="monospace">
                {formatTokens(tokens)} tokens · {count} request
            </Typography>
        </Box>
    );
};


const AVATAR_COLORS = [
    '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b',
    '#10b981', '#3b82f6', '#ef4444', '#14b8a6',
];

function avatarColor(userId = '') {
    const hash = [...userId].reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

const UserRow = ({ user }) => {
    const [open, setOpen] = useState(false);
    const hasFeatures = user.features?.length > 0;
    const initials = userInitials(user.name, user.userId);

    return (
        <Box>
            <Box
                onClick={() => hasFeatures && setOpen((v) => !v)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 1.5, py: 1,
                    cursor: hasFeatures ? 'pointer' : 'default',
                    '&:hover': hasFeatures ? { bgcolor: 'action.hover' } : {},
                    transition: 'background 0.12s',
                }}
            >
                <Box sx={{
                    width: 30, height: 30, borderRadius: '50%',
                    bgcolor: avatarColor(user.userId),
                    color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700, flexShrink: 0, letterSpacing: 0.5,
                }}>
                    {initials || <PersonOutline sx={{ fontSize: 14 }} />}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                        {user.name}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <TokenOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
                    <Typography variant="caption" fontFamily="monospace" fontWeight={500}>
                        {formatTokens(user.totalTokens)}
                    </Typography>
                </Box>

                <Typography
                    variant="caption" color="text.secondary"
                    fontFamily="monospace"
                    sx={{ flexShrink: 0, minWidth: 44, textAlign: 'right' }}
                >
                    {user.requestCount} req
                </Typography>

                {hasFeatures && (
                    <Box sx={{ color: 'text.disabled', display: 'flex', flexShrink: 0, ml: -0.5 }}>
                        {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                    </Box>
                )}
            </Box>

            {hasFeatures && (
                <Collapse in={open} unmountOnExit>
                    <Box sx={{ pl: 6.5, pr: 1.5, pb: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {user.features.map((f) => (
                            <FeatureBadge
                                key={f.featureKey}
                                featureKey={f.featureKey}
                                count={f.requestCount}
                                tokens={f.totalTokens}
                            />
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};

const LoadingSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Skeleton variant="rounded" height={80} sx={{ flex: 1, borderRadius: 2 }} />
            <Skeleton variant="rounded" height={80} sx={{ flex: 1, borderRadius: 2 }} />
        </Box>
        {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: 1.5 }} />)}
    </Box>
);

const EmptyState = () => (
    <Box sx={{
        py: 5, textAlign: 'center',
        border: '1px dashed', borderColor: 'divider',
        borderRadius: 2,
    }}>
        <QueryStatsOutlined sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
            No usage recorded
        </Typography>
        <Typography variant="caption" color="text.disabled">
            AI activity will appear here once users start making requests.
        </Typography>
    </Box>
);

const AiUsageStats = ({ projectId, usersMap = {} }) => {
    const [period, setPeriod] = useState('day');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await getAIUsage(projectId, period);
            setData(res);
        } catch (err) {
            setError(err?.message || 'Failed to load usage data.');
        } finally {
            setLoading(false);
        }
    }, [projectId, period]);

    useEffect(() => { load(); }, [load]);

    const grouped = transformUsage(data?.usage ?? [], usersMap);
    const totalTokens = grouped.totalTokens;
    const totalReqs = grouped.requestCount;
    const userRows = grouped.users;

    const dailyLimit = data?.limits?.daily ?? null;
    const monthlyLimit = data?.limits?.monthly ?? null;
    const activeLimit = period === 'day' ? dailyLimit : monthlyLimit;

    const pctUsed = pct(totalTokens, activeLimit);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" fontWeight={600}>Usage</Typography>
                    </Box>
                    {activeLimit && (
                        <Typography variant="caption" color={pctUsed >= 90 ? 'error.main' : 'text.disabled'}>
                            {pctUsed}% of {period === 'day' ? 'daily' : 'monthly'} limit used
                        </Typography>
                    )}
                </Box>
                <ToggleButtonGroup
                    exclusive size="small"
                    value={period}
                    onChange={(_, val) => { if (val) setPeriod(val); }}
                >
                    {[['day', 'Today'], ['month', 'This month']].map(([val, label]) => (
                        <ToggleButton
                            key={val} value={val}
                            sx={{
                                px: 1.5, py: 0.4,
                                fontSize: '12px', fontWeight: 500,
                                textTransform: 'none',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main', color: '#fff',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                },
                            }}
                        >
                            {label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>
            {loading && !data ? (
                <LoadingSkeleton />
            ) : error ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="error.main">{error}</Typography>
                    <Typography
                        variant="caption" color="text.disabled"
                        sx={{ cursor: 'pointer', textDecoration: 'underline', mt: 0.5, display: 'block' }}
                        onClick={load}
                    >
                        Try again
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <StatCard
                            icon={<TokenOutlined sx={{ fontSize: 15 }} />}
                            label="Tokens used"
                            value={totalTokens}
                            limit={activeLimit}
                            sublabel={activeLimit ? `limit: ${formatTokens(activeLimit)}` : 'No limit set'}
                            mono
                        />
                        <StatCard
                            icon={<QueryStatsOutlined sx={{ fontSize: 15 }} />}
                            label="Total requests"
                            value={totalReqs}
                            sublabel={`${userRows.length} active user${userRows.length !== 1 ? 's' : ''}`}
                        />
                    </Box>

                    {userRows.length === 0 ? <EmptyState /> : (
                        <Box sx={{
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 2, overflow: 'hidden',
                        }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center',
                                px: 1.5, py: 0.875,
                                bgcolor: 'background.default',
                                borderBottom: '1px solid', borderColor: 'divider',
                            }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1 }}>
                                    User
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 1.5 }}>
                                    Tokens
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 52, textAlign: 'right' }}>
                                    Requests
                                </Typography>
                                <Box sx={{ width: 20, ml: 0.5 }} />
                            </Box>

                            {userRows.map((user, i) => (
                                <React.Fragment key={user.userId ?? i}>
                                    {i > 0 && <Divider />}
                                    <UserRow user={user} />
                                </React.Fragment>
                            ))}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default AiUsageStats;